// ----------------------------------------------------------------------
// Normalización de las respuestas de la API CIE-11 al DTO propio.
//
// El `title` de /search llega con marcado `<em class='found'>` incrustado.
// Se convierte en segmentos para pintar el resaltado con elementos reales y
// NUNCA con dangerouslySetInnerHTML: el texto viene de un servicio externo.
// ----------------------------------------------------------------------

// `&amp;` se decodifica al final: si no, `&amp;lt;` acabaría siendo `<`.
const ENTIDADES = [
  [/&lt;/gi, '<'],
  [/&gt;/gi, '>'],
  [/&quot;/gi, '"'],
  [/&apos;/gi, "'"],
  [/&#0*39;/g, "'"],
  [/&nbsp;/gi, ' '],
  [/&amp;/gi, '&'],
];

const decodificarEntidades = (texto) =>
  ENTIDADES.reduce((acumulado, [patron, valor]) => acumulado.replace(patron, valor), texto);

const quitarEtiquetas = (texto) => texto.replace(/<[^>]*>/g, '');

const normalizarEspacios = (texto) => texto.replace(/\s+/g, ' ').trim();

const comoTexto = (valor) => (typeof valor === 'string' ? valor : '');

/**
 * Parte un título con marcado en segmentos consecutivos para pintar en negrita
 * lo que coincidió con la búsqueda.
 * @param {string} tituloHtml título tal cual lo devuelve /search
 * @returns {Array<{texto: string, resaltado: boolean}>}
 */
export function segmentosResaltados(tituloHtml) {
  const crudo = comoTexto(tituloHtml);
  if (!crudo) return [];

  const partes = crudo.split(/(<\/?em[^>]*>)/gi);
  const segmentos = [];
  let profundidad = 0;

  partes.forEach((parte) => {
    if (/^<em[^>]*>$/i.test(parte)) {
      profundidad += 1;
      return;
    }
    if (/^<\/em\s*>$/i.test(parte)) {
      profundidad = Math.max(0, profundidad - 1);
      return;
    }

    const texto = decodificarEntidades(quitarEtiquetas(parte));
    if (!texto) return;

    const resaltado = profundidad > 0;
    const anterior = segmentos[segmentos.length - 1];

    // Fusionar con el segmento previo si comparte estado evita nodos partidos
    // cuando la API cierra y reabre el <em> dentro de la misma palabra.
    if (anterior && anterior.resaltado === resaltado) anterior.texto += texto;
    else segmentos.push({ texto, resaltado });
  });

  return segmentos;
}

/**
 * El mismo título sin marcado, sin entidades y con espacios normalizados.
 * @param {string} tituloHtml
 * @returns {string}
 */
export function textoLimpio(tituloHtml) {
  const crudo = comoTexto(tituloHtml);
  if (!crudo) return '';
  return normalizarEspacios(decodificarEntidades(quitarEtiquetas(crudo)));
}

/**
 * Formato histórico que ya está persistido en la base de datos:
 * "BA00.Z - HIPERTENSIÓN ESENCIAL, SIN ESPECIFICACIÓN". No se cambia.
 * @param {{codigo?: string, titulo?: string}} diagnostico
 * @returns {string}
 */
export function valorParaFormulario(diagnostico) {
  if (!diagnostico) return '';

  const titulo = textoLimpio(diagnostico.titulo).toUpperCase();
  const codigo = comoTexto(diagnostico.codigo).trim();

  if (!codigo) return titulo;
  if (!titulo) return codigo;
  return `${codigo} - ${titulo}`;
}

/**
 * Identificador de una URI de la OMS: el id numérico MÁS su cola residual
 * (`1234`, `1234/unspecified`, `1234/other`). Las URIs son canónicas de la nube
 * (`http://id.who.int/...`): solo se usa esta parte contra el host local.
 *
 * La cola importa: `.Z` y `.Y` son entidades propias con su código y su título.
 * Recortarla devolvía el id del PADRE, y BD11.Z acababa guardado con el título
 * de BD11 en la historia clínica.
 * @param {string} [uri]
 * @returns {string|null}
 */
export function idDesdeUri(uri) {
  const crudo = comoTexto(uri);
  if (!crudo) return null;

  const segmentos = crudo.split(/[/#?]/).filter(Boolean);
  for (let i = segmentos.length - 1; i >= 0; i -= 1) {
    if (/^\d+$/.test(segmentos[i])) {
      // Solo se acepta como cola la palabra del residual; cualquier otra cosa
      // (`/release/11/2025-01/mms`) no es una entidad y se queda en el número.
      const cola = segmentos.slice(i + 1);
      return cola.every((parte) => /^[a-z]+$/i.test(parte))
        ? [segmentos[i], ...cola].join('/')
        : segmentos[i];
    }
  }
  return null;
}

/**
 * ISimpleEntity de /search -> Diagnostico.
 *
 * `sinonimoCoincidente` solo se rellena cuando el título NO fue lo que coincidió
 * (`titleIsASearchResult: false`): es el caso de "presión alta" → "Hipertensión
 * esencial", donde sin explicar el sinónimo el resultado parece un fallo.
 * @param {object} entidad
 * @returns {object} Diagnostico
 */
export function normalizarResultado(entidad) {
  const origen = entidad || {};
  const coincidencias = Array.isArray(origen.matchingPVs) ? origen.matchingPVs : [];

  let sinonimoCoincidente = null;
  if (origen.titleIsASearchResult === false && coincidencias.length > 0) {
    const mejor = coincidencias.reduce((maximo, actual) =>
      (actual?.score ?? 0) > (maximo?.score ?? 0) ? actual : maximo
    );
    sinonimoCoincidente = textoLimpio(mejor?.label) || null;
  }

  return {
    id: idDesdeUri(origen.id),
    codigo: comoTexto(origen.theCode).trim(),
    titulo: textoLimpio(origen.title),
    tituloHtml: comoTexto(origen.title),
    capitulo: comoTexto(origen.chapter).trim(),
    esHoja: Boolean(origen.isLeaf),
    puntaje: typeof origen.score === 'number' ? origen.score : 0,
    sinonimoCoincidente,
    esResidualNoEspecificado: Boolean(origen.isResidualUnspecified),
    esResidualOtro: Boolean(origen.isResidualOther),
  };
}

// Un código CIE-11 siempre mezcla letra y dígito ("CA23", "BA00.Z", "5A11"),
// lo que basta para distinguirlo de una palabra escrita ("asma", "dolor").
const FORMA_CODIGO = /^[A-Z0-9](?:[A-Z0-9]|\.(?=[A-Z0-9]))*$/;

/**
 * ¿El texto tecleado tiene forma de código CIE-11? /search no encuentra códigos,
 * hay que desviarlos a /codeinfo.
 * @param {string} texto
 * @returns {boolean}
 */
export function esCodigoCie(texto) {
  const candidato = comoTexto(texto).trim().toUpperCase();
  if (candidato.length < 2 || candidato.length > 8) return false;
  if (!FORMA_CODIGO.test(candidato)) return false;
  return /\d/.test(candidato) && /[A-Z]/.test(candidato);
}

/**
 * La API busca por palabras completas: "apen" devuelve 0 resultados y "apen%"
 * devuelve 50. El comodín en la última palabra es obligatorio para un
 * type-ahead y no degrada las palabras ya completas.
 * @param {string} texto
 * @returns {string}
 */
export function consultaConComodin(texto) {
  const limpio = normalizarEspacios(comoTexto(texto));
  if (!limpio) return '';
  return limpio.endsWith('%') ? limpio : `${limpio}%`;
}
